import { useCallback, useEffect, useState } from "react";
import { useDataProvider, type Identifier } from "ra-core";

import { EXPEDIENTE_ACCESS_DENIED_MESSAGE } from "../../providers/commons/contactExpediente";
import type { CrmDataProvider } from "../../providers/types";
import type {
  ContactExpedienteDocType,
  ContactExpedienteInput,
} from "../../types";

/** True when the provider refused the five-audience gate (the SQL/RLS stays the authority). */
export const isExpedienteDenied = (error: unknown): boolean =>
  error instanceof Error && error.message === EXPEDIENTE_ACCESS_DENIED_MESSAGE;

type ReadState<T> =
  | { status: "pending" }
  | { status: "ready"; data: T }
  | { status: "error"; error: unknown };

/**
 * Component-state read with a manual `reload`. Deliberately NOT react-query: the mobile app
 * persists the whole query cache to localStorage, and an expediente value must never reach it.
 * The data lives only in this component's state and is gone when the tab closes.
 */
const useSensitiveRead = <T>(read: () => Promise<T>) => {
  const [state, setState] = useState<ReadState<T>>({ status: "pending" });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    read().then(
      (data) => {
        if (!cancelled) setState({ status: "ready", data });
      },
      (error: unknown) => {
        if (!cancelled) setState({ status: "error", error });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [read, nonce]);

  /** Re-reads; keeps the current data on screen until the new one arrives. */
  const reload = useCallback(() => setNonce((current) => current + 1), []);
  /** Retry after an error: back to the loading state first. */
  const retry = useCallback(() => {
    setState({ status: "pending" });
    setNonce((current) => current + 1);
  }, []);
  return { state, reload, retry };
};

/**
 * Reads a contact's expediente (and its documents) once the tab is mounted, and exposes the write
 * actions of the tab. Every write re-reads so the header and the documents stay true.
 */
export const useContactExpediente = (contactId: Identifier) => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const read = useCallback(
    () => dataProvider.getContactExpediente(contactId),
    [dataProvider, contactId],
  );
  const { state, reload, retry } = useSensitiveRead(read);

  const save = useCallback(
    async (input: ContactExpedienteInput) => {
      const saved = await dataProvider.saveContactExpediente(contactId, input);
      reload();
      return saved;
    },
    [dataProvider, contactId, reload],
  );

  const upload = useCallback(
    async (docType: ContactExpedienteDocType, file: File) => {
      await dataProvider.uploadContactExpedienteDocument({
        contactId,
        docType,
        file,
      });
      reload();
    },
    [dataProvider, contactId, reload],
  );

  const remove = useCallback(
    async (documentId: Identifier, reason: string) => {
      await dataProvider.removeContactExpedienteDocument(documentId, reason);
      reload();
    },
    [dataProvider, reload],
  );

  /** A short-lived signed URL (rejects with a Spanish message, e.g. in the demo). */
  const getSignedUrl = useCallback(
    (documentId: Identifier) =>
      dataProvider.getContactExpedienteDocumentUrl(documentId),
    [dataProvider],
  );

  return {
    bundle: state.status === "ready" ? state.data : undefined,
    isPending: state.status === "pending",
    error: state.status === "error" ? state.error : undefined,
    retry,
    save,
    upload,
    remove,
    getSignedUrl,
  };
};

/** The labels (never values) of what is missing, for roles outside the five audiences. */
export const useContactExpedienteMissing = (contactId: Identifier) => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const read = useCallback(
    () => dataProvider.getContactExpedienteMissing(contactId),
    [dataProvider, contactId],
  );
  const { state, retry } = useSensitiveRead(read);
  return {
    isPending: state.status === "pending",
    isError: state.status === "error",
    data: state.status === "ready" ? state.data : [],
    retry,
  };
};
